import { Button, Checkbox, DatePicker, Dropdown, Input, Tabs, Tooltip } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { listingSettingsService, type ListingKey } from '../services/listingSettings';

type SearchableColumn<T> = ColumnType<T> & {
  /**
   * Optional custom key for identifying this column's filter.
   * Falls back to column.key or dataIndex.
   */
  searchKey?: string;
  /**
   * Enable/disable header search input for this column.
   * Defaults to true.
   */
  searchable?: boolean;
  /**
   * Custom way to get a string value from a record for filtering.
   * If not provided, the hook will try to read from dataIndex.
   */
  getSearchValue?: (record: T) => string | number | null | undefined;
  /**
   * Optional operator type for this column's filter input.
   * Kept for backward compatibility, but operator list is now unified.
   */
  searchOperatorType?: 'number' | 'word';
};

export interface TableColumnSearchResult<T> {
  columnsWithSearch: ColumnsType<T>;
  filteredData: T[];
  searchRowCells?: React.ReactNode[];
}

interface TableColumnSearchOptions {
  splitHeader?: boolean;
  listingKey?: ListingKey;
}

type OperatorKey =
  | 'eq'
  | 'neq'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'like'
  | 'not_like'
  | 'contains'
  | 'not_contains'
  | 'begins'
  | 'ends';

const numberOperators: { value: OperatorKey; label: string }[] = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Does not equal' },
  { value: 'lt', label: 'Is less than' },
  { value: 'lte', label: 'Is less than or equal to' },
  { value: 'gt', label: 'Is greater than' },
  { value: 'gte', label: 'Is greater than or equal to' },
];

const wordOperators: { value: OperatorKey; label: string }[] = [
  { value: 'like', label: 'Is like' },
  { value: 'not_like', label: 'Is not like' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'begins', label: 'Begins with' },
  { value: 'ends', label: 'Ends with' },
];

const allOperators: { value: OperatorKey; label: string }[] = [
  ...numberOperators,
  ...wordOperators,
];

type NumericOperatorKey = 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte' | 'between';
type DateOperatorKey = 'on' | 'before' | 'after' | 'between';

const numericOperators: { value: NumericOperatorKey; label: string }[] = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Does not equal' },
  { value: 'lt', label: 'Is less than' },
  { value: 'lte', label: 'Is less than or equal to' },
  { value: 'gt', label: 'Is greater than' },
  { value: 'gte', label: 'Is greater than or equal to' },
  { value: 'between', label: 'Is between' },
];

const dateOperators: { value: DateOperatorKey; label: string }[] = [
  { value: 'on', label: 'On' },
  { value: 'before', label: 'Before' },
  { value: 'after', label: 'After' },
  { value: 'between', label: 'Between' },
];

const operatorPalette = {
  number: '#16a34a',
  numberAlt: '#0ea5e9',
  word: '#2563eb',
  wordAlt: '#7c3aed',
  negative: '#dc2626',
  percent: '#16a34a',
};

const buildSvgIcon = (text: string, color: string) => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <text
      x="9"
      y="11.2"
      textAnchor="middle"
      fontSize="7"
      fontWeight="700"
      fontFamily="Arial, sans-serif"
      fill={color}
    >
      {text}
    </text>
  </svg>
);

const buildLikeIcon = (percentColor: string) => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <text x="5" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      A
    </text>
    <text x="9" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={percentColor}>
      %
    </text>
    <text x="13" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      C
    </text>
  </svg>
);

const buildContainsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <text x="5" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      A
    </text>
    <rect x="7.1" y="4.4" width="3.8" height="9.2" rx="1.2" fill={operatorPalette.percent} />
    <text x="9" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill="#ffffff">
      B
    </text>
    <text x="13" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      C
    </text>
  </svg>
);

const buildNotContainsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <text x="5" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      A
    </text>
    <text x="9" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      C
    </text>
    <rect x="11.1" y="4.4" width="3.8" height="9.2" rx="1.2" fill={operatorPalette.negative} />
    <text x="13" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill="#ffffff">
      B
    </text>
  </svg>
);

const buildBeginsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <rect x="3.1" y="4.4" width="3.8" height="9.2" rx="1.2" fill={operatorPalette.word} />
    <text x="5" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill="#ffffff">
      A
    </text>
    <text x="9" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      B
    </text>
    <text x="13" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      C
    </text>
  </svg>
);

const buildEndsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <text x="5" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      A
    </text>
    <text x="9" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={operatorPalette.word}>
      B
    </text>
    <rect x="11.1" y="4.4" width="3.8" height="9.2" rx="1.2" fill={operatorPalette.word} />
    <text x="13" y="11.2" textAnchor="middle" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif" fill="#ffffff">
      C
    </text>
  </svg>
);

const operatorIcons: Record<OperatorKey, React.ReactNode> = {
  eq: buildSvgIcon('=', operatorPalette.number),
  neq: buildSvgIcon('≠', operatorPalette.negative),
  lt: buildSvgIcon('<', operatorPalette.numberAlt),
  lte: buildSvgIcon('≤', operatorPalette.numberAlt),
  gt: buildSvgIcon('>', operatorPalette.numberAlt),
  gte: buildSvgIcon('≥', operatorPalette.numberAlt),
  like: buildLikeIcon(operatorPalette.percent),
  not_like: buildLikeIcon(operatorPalette.negative),
  contains: buildContainsIcon(),
  not_contains: buildNotContainsIcon(),
  begins: buildBeginsIcon(),
  ends: buildEndsIcon(),
};

const numericOperatorIcons: Record<NumericOperatorKey, React.ReactNode> = {
  eq: operatorIcons.eq,
  neq: operatorIcons.neq,
  lt: operatorIcons.lt,
  lte: operatorIcons.lte,
  gt: operatorIcons.gt,
  gte: operatorIcons.gte,
  between: buildSvgIcon('↔', operatorPalette.numberAlt),
};

const dateOperatorIcons: Record<DateOperatorKey, React.ReactNode> = {
  on: buildSvgIcon('=', operatorPalette.wordAlt),
  before: buildSvgIcon('<', operatorPalette.wordAlt),
  after: buildSvgIcon('>', operatorPalette.wordAlt),
  between: buildSvgIcon('↔', operatorPalette.wordAlt),
};

const headerContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  paddingBottom: 4,
};

const headerLabelRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
};

const headerLabelStyle: React.CSSProperties = {
  fontWeight: 600,
};

const buildFilterIcon = (active: boolean) => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M2 3h12l-4.6 5.2v4.2l-2.8-1.6V8.2L2 3z"
      fill={active ? '#2563eb' : '#9ca3af'}
    />
  </svg>
);

const buildToggleIcon = (expanded: boolean) => (
  <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
    <rect x="1" y="1" width="16" height="16" rx="3" fill="#f9fafb" stroke="#d1d5db" />
    <text
      x="9"
      y="11.2"
      textAnchor="middle"
      fontSize="9"
      fontWeight="700"
      fontFamily="Arial, sans-serif"
      fill="#6b7280"
    >
      {expanded ? '-' : '+'}
    </text>
  </svg>
);

const normalizeValueLabel = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const parseNumericValue = (value: unknown) => {
  const parsed = Number.parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDateValue = (value: unknown) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const str = String(value ?? '').trim();
  if (!str) return null;
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (isoMatch) {
    const date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const dmyMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str);
  if (dmyMatch) {
    const date = new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const buildDateTree = (values: string[]) => {
  const parsedEntries = values
    .filter((value) => value !== '-')
    .map((value) => ({ value, parsed: parseDateValue(value) }));
  const parsedCount = parsedEntries.filter((item) => item.parsed).length;
  const isDateValues =
    parsedEntries.length > 0 && parsedCount >= Math.max(1, Math.floor(parsedEntries.length * 0.6));
  if (!isDateValues) {
    return { isDateValues: false as const, tree: null as null };
  }

  const tree = new Map<number, Map<number, Map<number, string[]>>>();
  parsedEntries.forEach(({ value, parsed }) => {
    if (!parsed) return;
    const year = parsed.getFullYear();
    const month = parsed.getMonth() + 1;
    const day = parsed.getDate();
    if (!tree.has(year)) tree.set(year, new Map());
    const monthMap = tree.get(year)!;
    if (!monthMap.has(month)) monthMap.set(month, new Map());
    const dayMap = monthMap.get(month)!;
    if (!dayMap.has(day)) dayMap.set(day, []);
    dayMap.get(day)!.push(value);
  });

  return { isDateValues: true as const, tree };
};

function getColumnKey<T>(col: SearchableColumn<T>, index: number): string {
  if (col.searchKey) return String(col.searchKey);
  if (col.key != null) return String(col.key);
  if (typeof col.dataIndex === 'string' || typeof col.dataIndex === 'number') {
    return String(col.dataIndex);
  }
  if (Array.isArray(col.dataIndex) && col.dataIndex.length) {
    return String(col.dataIndex.join('.'));
  }
  return String(index);
}

function getDeepValue(record: any, dataIndex?: ColumnType<any>['dataIndex']): unknown {
  if (!dataIndex) return undefined;

  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((acc, key) => (acc == null ? undefined : acc[key]), record);
  }

  return record?.[dataIndex];
}

export function useTableColumnSearch<T>(
  data: T[],
  columns: SearchableColumn<T>[],
  searchColumns: SearchableColumn<T>[] = columns,
  options?: TableColumnSearchOptions
): TableColumnSearchResult<T> {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [columnOperators, setColumnOperators] = useState<Record<string, OperatorKey>>({});
  const [valuesSearchByKey, setValuesSearchByKey] = useState<Record<string, string>>({});
  const [selectedValuesByKey, setSelectedValuesByKey] = useState<Record<string, string[] | undefined>>({});
  const [textFiltersByKey, setTextFiltersByKey] = useState<Record<string, { operator: OperatorKey; value: string }>>({});
  const [numberFiltersByKey, setNumberFiltersByKey] = useState<Record<string, { operator: NumericOperatorKey; value: string; value2: string }>>({});
  const [dateFiltersByKey, setDateFiltersByKey] = useState<Record<string, { operator: DateOperatorKey; value: string; value2: string }>>({});
  const [filterOpenByKey, setFilterOpenByKey] = useState<Record<string, boolean>>({});
  const [dateTreeOpenByKey, setDateTreeOpenByKey] = useState<Record<string, { years: Record<string, boolean>; months: Record<string, boolean> }>>({});
  const splitHeader = options?.splitHeader ?? false;
  const listingKey = options?.listingKey;
  const persistTimerRef = useRef<number | null>(null);
  const hasHydratedRef = useRef(false);
  const loadFailedRef = useRef(false);

  const getDefaultOperator = (): OperatorKey => 'contains';

  const getColumnValue = (record: T, col: SearchableColumn<T>) => {
    if (typeof col.getSearchValue === 'function') {
      return col.getSearchValue(record);
    }
    return getDeepValue(record, col.dataIndex);
  };

  const valueOptionsByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    columns.forEach((col, index) => {
      const key = getColumnKey(col, index);
      const values = new Set<string>();
      data.forEach((record) => {
        const rawValue = getColumnValue(record, col);
        values.add(normalizeValueLabel(rawValue));
      });
      const sortedValues = Array.from(values).sort((a, b) => a.localeCompare(b));
      map.set(key, sortedValues);
    });
    return map;
  }, [columns, data]);

  const allowedKeys = useMemo(
    () => new Set(columns.map((col, index) => getColumnKey(col, index))),
    [columns]
  );

  const operatorKeys = useMemo<Set<OperatorKey>>(
    () => new Set(allOperators.map((op) => op.value)),
    []
  );
  const numericOperatorKeys = useMemo<Set<NumericOperatorKey>>(
    () => new Set(numericOperators.map((op) => op.value)),
    []
  );
  const dateOperatorKeys = useMemo<Set<DateOperatorKey>>(
    () => new Set(dateOperators.map((op) => op.value)),
    []
  );

  const hasActiveFilters = useMemo(() => {
    return (
      Object.values(columnFilters).some((value) => value) ||
      Object.values(selectedValuesByKey).some((values) => values !== undefined) ||
      Object.values(textFiltersByKey).some((filter) => filter?.value) ||
      Object.values(numberFiltersByKey).some((filter) => filter?.value || filter?.value2) ||
      Object.values(dateFiltersByKey).some((filter) => filter?.value || filter?.value2)
    );
  }, [columnFilters, selectedValuesByKey, textFiltersByKey, numberFiltersByKey, dateFiltersByKey]);

  useEffect(() => {
    if (!listingKey) {
      return;
    }
    if (hasHydratedRef.current) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        if (hasActiveFilters) {
          hasHydratedRef.current = true;
          return;
        }
        const settings = await listingSettingsService.getListingSettings(listingKey);
        if (cancelled) {
          return;
        }
        if (!settings?.column_filters) {
          hasHydratedRef.current = true;
          return;
        }
        const stored = settings.column_filters as Record<string, any>;
        const nextColumnFilters: Record<string, string> = {};
        const nextColumnOperators: Record<string, OperatorKey> = {};
        const nextSelectedValues: Record<string, string[] | undefined> = {};
        const nextTextFilters: Record<string, { operator: OperatorKey; value: string }> = {};
        const nextNumberFilters: Record<string, { operator: NumericOperatorKey; value: string; value2: string }> = {};
        const nextDateFilters: Record<string, { operator: DateOperatorKey; value: string; value2: string }> = {};

        const storedColumnFilters = stored?.columnFilters;
        if (storedColumnFilters && typeof storedColumnFilters === 'object') {
          Object.entries(storedColumnFilters).forEach(([key, value]) => {
            if (!allowedKeys.has(key) || typeof value !== 'string') return;
            if (!value.trim()) return;
            nextColumnFilters[key] = value;
          });
        }

        const storedColumnOperators = stored?.columnOperators;
        if (storedColumnOperators && typeof storedColumnOperators === 'object') {
          Object.entries(storedColumnOperators).forEach(([key, value]) => {
            if (!allowedKeys.has(key)) return;
            if (typeof value !== 'string') return;
            const operator = value as OperatorKey;
            if (!operatorKeys.has(operator)) return;
            nextColumnOperators[key] = operator;
          });
        }

        const storedSelectedValues = stored?.selectedValuesByKey;
        if (storedSelectedValues && typeof storedSelectedValues === 'object') {
          Object.entries(storedSelectedValues).forEach(([key, value]) => {
            if (!allowedKeys.has(key) || value === null || value === undefined) return;
            if (!Array.isArray(value)) return;
            const filtered = value.filter((item) => typeof item === 'string');
            if (filtered.length === 0) return;
            nextSelectedValues[key] = filtered;
          });
        }

        const storedTextFilters = stored?.textFiltersByKey;
        if (storedTextFilters && typeof storedTextFilters === 'object') {
          Object.entries(storedTextFilters).forEach(([key, value]) => {
            if (!allowedKeys.has(key) || !value || typeof value !== 'object') return;
            const operator = (value as any).operator as OperatorKey | undefined;
            const filterValue = (value as any).value;
            if (!operator || !operatorKeys.has(operator)) return;
            if (typeof filterValue !== 'string' || !filterValue.trim()) return;
            nextTextFilters[key] = { operator, value: filterValue };
          });
        }

        const storedNumberFilters = stored?.numberFiltersByKey;
        if (storedNumberFilters && typeof storedNumberFilters === 'object') {
          Object.entries(storedNumberFilters).forEach(([key, value]) => {
            if (!allowedKeys.has(key) || !value || typeof value !== 'object') return;
            const operator = (value as any).operator as NumericOperatorKey | undefined;
            const filterValue = (value as any).value;
            const filterValue2 = (value as any).value2;
            if (!operator || !numericOperatorKeys.has(operator)) return;
            if (typeof filterValue !== 'string' || typeof filterValue2 !== 'string') return;
            if (!filterValue.trim() && !filterValue2.trim()) return;
            nextNumberFilters[key] = {
              operator,
              value: filterValue,
              value2: filterValue2,
            };
          });
        }

        const storedDateFilters = stored?.dateFiltersByKey;
        if (storedDateFilters && typeof storedDateFilters === 'object') {
          Object.entries(storedDateFilters).forEach(([key, value]) => {
            if (!allowedKeys.has(key) || !value || typeof value !== 'object') return;
            const operator = (value as any).operator as DateOperatorKey | undefined;
            const filterValue = (value as any).value;
            const filterValue2 = (value as any).value2;
            if (!operator || !dateOperatorKeys.has(operator)) return;
            if (typeof filterValue !== 'string' || typeof filterValue2 !== 'string') return;
            if (!filterValue.trim() && !filterValue2.trim()) return;
            nextDateFilters[key] = {
              operator,
              value: filterValue,
              value2: filterValue2,
            };
          });
        }

        if (Object.keys(nextColumnFilters).length) setColumnFilters(nextColumnFilters);
        if (Object.keys(nextColumnOperators).length) setColumnOperators(nextColumnOperators);
        if (Object.keys(nextSelectedValues).length) setSelectedValuesByKey(nextSelectedValues);
        if (Object.keys(nextTextFilters).length) setTextFiltersByKey(nextTextFilters);
        if (Object.keys(nextNumberFilters).length) setNumberFiltersByKey(nextNumberFilters);
        if (Object.keys(nextDateFilters).length) setDateFiltersByKey(nextDateFilters);

        hasHydratedRef.current = true;
        loadFailedRef.current = false;
      } catch {
        hasHydratedRef.current = true;
        loadFailedRef.current = true;
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    listingKey,
    allowedKeys,
    operatorKeys,
    numericOperatorKeys,
    dateOperatorKeys,
  ]);

  useEffect(() => {
    if (!listingKey) {
      return;
    }
    if (!hasHydratedRef.current) {
      return;
    }
    if (loadFailedRef.current && !hasActiveFilters) {
      return;
    }
    if (persistTimerRef.current) {
      window.clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = window.setTimeout(async () => {
      const columnFiltersPayload = Object.fromEntries(
        Object.entries(columnFilters).filter(([, value]) => typeof value === 'string' && value.trim() !== '')
      );
      const columnOperatorsPayload = Object.fromEntries(
        Object.entries(columnOperators).filter(([key]) => key in columnFiltersPayload)
      );
      const selectedValuesPayload = Object.fromEntries(
        Object.entries(selectedValuesByKey).filter(
          ([, value]) => Array.isArray(value) && value.length > 0
        )
      );
      const textFiltersPayload = Object.fromEntries(
        Object.entries(textFiltersByKey).filter(([, value]) => value?.value && value.value.trim() !== '')
      );
      const numberFiltersPayload = Object.fromEntries(
        Object.entries(numberFiltersByKey).filter(
          ([, value]) => (value?.value && value.value.trim() !== '') || (value?.value2 && value.value2.trim() !== '')
        )
      );
      const dateFiltersPayload = Object.fromEntries(
        Object.entries(dateFiltersByKey).filter(
          ([, value]) => (value?.value && value.value.trim() !== '') || (value?.value2 && value.value2.trim() !== '')
        )
      );

      const payload = {
        columnFilters: columnFiltersPayload,
        columnOperators: columnOperatorsPayload,
        selectedValuesByKey: selectedValuesPayload,
        textFiltersByKey: textFiltersPayload,
        numberFiltersByKey: numberFiltersPayload,
        dateFiltersByKey: dateFiltersPayload,
      };

      const hasAny =
        Object.keys(columnFiltersPayload).length ||
        Object.keys(selectedValuesPayload).length ||
        Object.keys(textFiltersPayload).length ||
        Object.keys(numberFiltersPayload).length ||
        Object.keys(dateFiltersPayload).length;

      try {
        await listingSettingsService.updateListingSettings(listingKey, {
          column_filters: hasAny ? payload : null,
        });
      } catch {
        // Ignore persistence errors
      }
    }, 500);
  }, [
    listingKey,
    columnFilters,
    columnOperators,
    selectedValuesByKey,
    textFiltersByKey,
    numberFiltersByKey,
    dateFiltersByKey,
    hasActiveFilters,
  ]);

  const buildFilterInput = (col: SearchableColumn<T>, index: number) => {
    const key = getColumnKey(col, index);
    const operatorOptions = allOperators;
    const selectedOperator = columnOperators[key] || getDefaultOperator();
    const selectedLabel = operatorOptions.find((option) => option.value === selectedOperator)?.label;
    const iconNode = selectedOperator ? operatorIcons[selectedOperator] : undefined;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <Dropdown
          trigger={['click']}
          dropdownRender={() => (
            <div
              style={{
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                borderRadius: 6,
                padding: 6,
                minWidth: 180,
              }}
            >
              {operatorOptions.map((option) => {
                const isActive = option.value === selectedOperator;
                const optionIcon = operatorIcons[option.value];
                return (
                  <div
                    key={option.value}
                    onClick={(event) => {
                      event.stopPropagation();
                      setColumnOperators((prev) => ({ ...prev, [key]: option.value }));
                    }}
                    style={{
                      padding: '6px 8px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                      background: isActive ? '#e5f0ff' : 'transparent',
                      color: isActive ? '#1f2937' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>{optionIcon}</span>
                    <span>{option.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        >
          <Tooltip title={selectedLabel}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              tabIndex={0}
              role="button"
              aria-label={selectedLabel || 'Filter operator'}
            >
              {iconNode ?? buildSvgIcon('?', '#6b7280')}
            </span>
          </Tooltip>
        </Dropdown>
        <Input
          size="small"
          placeholder="Search"
          allowClear
          value={columnFilters[key] || ''}
          onChange={(e) => {
            const value = e.target.value;
            setColumnFilters((prev) => ({
              ...prev,
              [key]: value,
            }));
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onFocus={(e) => {
            e.stopPropagation();
          }}
        />
      </div>
    );
  };

  const isColumnFilterActive = (key: string) => {
    if (columnFilters[key]) return true;
    const allValues = valueOptionsByKey.get(key) ?? [];
    const selectedValues = selectedValuesByKey[key];
    if (selectedValues !== undefined) {
      if (selectedValues.length === 0) return true;
      if (selectedValues.length < allValues.length) return true;
    }
    const textFilter = textFiltersByKey[key];
    if (textFilter?.value) return true;
    const numberFilter = numberFiltersByKey[key];
    if (numberFilter?.value || numberFilter?.value2) return true;
    const dateFilter = dateFiltersByKey[key];
    if (dateFilter?.value || dateFilter?.value2) return true;
    return false;
  };

  const clearColumnFilters = (key: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: '' }));
    setSelectedValuesByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setValuesSearchByKey((prev) => ({ ...prev, [key]: '' }));
    setTextFiltersByKey((prev) => ({
      ...prev,
      [key]: { operator: 'contains', value: '' },
    }));
    setNumberFiltersByKey((prev) => ({
      ...prev,
      [key]: { operator: 'eq', value: '', value2: '' },
    }));
    setDateFiltersByKey((prev) => ({
      ...prev,
      [key]: { operator: 'on', value: '', value2: '' },
    }));
  };

  const buildAdvancedFilterDropdown = (col: SearchableColumn<T>, index: number) => {
    const key = getColumnKey(col, index);
    const allValues = valueOptionsByKey.get(key) ?? [];
    const valuesSearch = valuesSearchByKey[key] || '';
    const selectedValues = selectedValuesByKey[key];
    const selectedSet = new Set(selectedValues ?? allValues);
    const isAllSelected = selectedValues === undefined || selectedSet.size === allValues.length;
    const isIndeterminate = !isAllSelected && selectedSet.size > 0;
    const filteredValues = valuesSearch
      ? allValues.filter((value) => value.toLowerCase().includes(valuesSearch.toLowerCase()))
      : allValues;

    const textFilter = textFiltersByKey[key] || { operator: 'contains', value: '' };
    const numberFilter = numberFiltersByKey[key] || { operator: 'eq', value: '', value2: '' };
    const dateFilter = dateFiltersByKey[key] || { operator: 'on', value: '', value2: '' };
    const { isDateValues, tree: dateTree } = buildDateTree(allValues);

    const renderOperatorSelect = <T extends { value: string; label: string }>(
      options: T[],
      value: string,
      onChange: (next: string) => void,
      iconMap: Record<string, React.ReactNode>
    ) => {
      const selectedLabel = options.find((option) => option.value === value)?.label || '';
      const selectedIcon = iconMap[value] ?? buildSvgIcon('?', '#6b7280');
      return (
        <Dropdown
          trigger={['click']}
          dropdownRender={() => (
            <div
              style={{
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                borderRadius: 6,
                padding: 6,
                minWidth: 180,
              }}
            >
              {options.map((option) => {
                const isActive = option.value === value;
                const optionIcon = iconMap[option.value] ?? buildSvgIcon('?', '#6b7280');
                return (
                  <div
                    key={option.value}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(option.value);
                    }}
                    style={{
                      padding: '6px 8px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                      background: isActive ? '#e5f0ff' : 'transparent',
                      color: isActive ? '#1f2937' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>{optionIcon}</span>
                    <span>{option.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '2px 6px',
              cursor: 'pointer',
              fontSize: 12,
              background: '#fff',
              height: 28,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>{selectedIcon}</span>
            <span style={{ color: '#111827' }}>{selectedLabel}</span>
          </div>
        </Dropdown>
      );
    };

    const openState = dateTreeOpenByKey[key] || { years: {}, months: {} };

    const toggleYear = (year: number) => {
      const yearKey = String(year);
      setDateTreeOpenByKey((prev) => {
        const current = prev[key] || { years: {}, months: {} };
        const nextValue = !current.years[yearKey];
        console.log('[DateTree] toggle year', { key, year, open: nextValue });
        return {
          ...prev,
          [key]: {
            years: { ...current.years, [yearKey]: nextValue },
            months: { ...current.months },
          },
        };
      });
    };

    const toggleMonth = (year: number, month: number) => {
      const monthKey = `${year}-${month}`;
      setDateTreeOpenByKey((prev) => {
        const current = prev[key] || { years: {}, months: {} };
        const nextValue = !current.months[monthKey];
        console.log('[DateTree] toggle month', { key, year, month, open: nextValue });
        return {
          ...prev,
          [key]: {
            years: { ...current.years },
            months: { ...current.months, [monthKey]: nextValue },
          },
        };
      });
    };

    const DateTree: React.FC<{
      tree: Map<number, Map<number, Map<number, string[]>>>;
      allValues: string[];
      selectedSet: Set<string>;
      onSelectionChange: (nextSelected: Set<string>) => void;
    }> = ({ tree, allValues, selectedSet, onSelectionChange }) => {
      const ensureSelection = (next: Set<string>) => {
        if (next.size === allValues.length) {
          onSelectionChange(new Set(allValues));
        } else {
          onSelectionChange(next);
        }
      };

      return (
        <>
          {Array.from(tree.entries())
            .sort(([a], [b]) => a - b)
            .map(([year, monthMap]) => {
              const yearValues: string[] = [];
              monthMap.forEach((dayMap) => {
                dayMap.forEach((values) => yearValues.push(...values));
              });
              const yearSelectedCount = yearValues.filter((value) => selectedSet.has(value)).length;
              const yearChecked = yearSelectedCount === yearValues.length && yearValues.length > 0;
              const yearIndeterminate = yearSelectedCount > 0 && !yearChecked;
              const yearKey = String(year);
              const yearOpen = openState.years[yearKey] ?? false;
              return (
                <div key={year} style={{ paddingLeft: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span
                      style={{
                        width: 12,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleYear(year);
                      }}
                    >
                      {buildToggleIcon(yearOpen)}
                    </span>
                    <Checkbox
                      indeterminate={yearIndeterminate}
                      checked={yearChecked}
                      onChange={(event) => {
                        const next = new Set(selectedSet);
                        if (event.target.checked) {
                          yearValues.forEach((value) => next.add(value));
                        } else {
                          yearValues.forEach((value) => next.delete(value));
                        }
                        ensureSelection(next);
                      }}
                    >
                      {year}
                    </Checkbox>
                  </div>
                  {yearOpen &&
                    Array.from(monthMap.entries())
                      .sort(([a], [b]) => a - b)
                      .map(([month, dayMap]) => {
                        const monthValues: string[] = [];
                        dayMap.forEach((values) => monthValues.push(...values));
                        const monthSelectedCount = monthValues.filter((value) => selectedSet.has(value)).length;
                        const monthChecked = monthSelectedCount === monthValues.length && monthValues.length > 0;
                        const monthIndeterminate = monthSelectedCount > 0 && !monthChecked;
                        const monthKey = `${year}-${month}`;
                        const monthOpen = openState.months[monthKey] ?? false;
                        return (
                          <div key={monthKey} style={{ paddingLeft: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span
                                style={{
                                  width: 12,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleMonth(year, month);
                                }}
                              >
                                {buildToggleIcon(monthOpen)}
                              </span>
                              <Checkbox
                                indeterminate={monthIndeterminate}
                                checked={monthChecked}
                                onChange={(event) => {
                                  const next = new Set(selectedSet);
                                  if (event.target.checked) {
                                    monthValues.forEach((value) => next.add(value));
                                  } else {
                                    monthValues.forEach((value) => next.delete(value));
                                  }
                                  ensureSelection(next);
                                }}
                              >
                                {new Date(year, month - 1, 1).toLocaleString('en', { month: 'long' })}
                              </Checkbox>
                            </div>
                            {monthOpen &&
                              Array.from(dayMap.entries())
                                .sort(([a], [b]) => a - b)
                                .map(([day, values]) => {
                                  const daySelectedCount = values.filter((value) => selectedSet.has(value)).length;
                                  const dayChecked = daySelectedCount === values.length && values.length > 0;
                                  const dayIndeterminate = daySelectedCount > 0 && !dayChecked;
                                  return (
                                    <div key={`${year}-${month}-${day}`} style={{ paddingLeft: 28 }}>
                                      <Checkbox
                                        indeterminate={dayIndeterminate}
                                        checked={dayChecked}
                                        onChange={(event) => {
                                          const next = new Set(selectedSet);
                                          if (event.target.checked) {
                                            values.forEach((value) => next.add(value));
                                          } else {
                                            values.forEach((value) => next.delete(value));
                                          }
                                          ensureSelection(next);
                                        }}
                                      >
                                        {day}
                                      </Checkbox>
                                    </div>
                                  );
                                })}
                          </div>
                        );
                      })}
                </div>
              );
            })}
        </>
      );
    };

    const openStateKey = `${Object.entries(openState.years)
      .map(([k, v]) => `${k}:${v ? 1 : 0}`)
      .join('|')}-${Object.entries(openState.months)
      .map(([k, v]) => `${k}:${v ? 1 : 0}`)
      .join('|')}`;

    const dropdownContent = (
      <div
        key={openStateKey}
        style={{
          width: 260,
          background: '#fff',
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          padding: 8,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <Tabs
          size="small"
          items={[
            {
              key: 'values',
              label: 'Values',
              children: (
                <div>
                  <Input
                    size="small"
                    placeholder="Search values"
                    value={valuesSearch}
                    onChange={(event) =>
                      setValuesSearchByKey((prev) => ({ ...prev, [key]: event.target.value }))
                    }
                    style={{ marginBottom: 8 }}
                  />
                  <div style={{ maxHeight: 180, overflowY: 'auto', paddingRight: 4 }}>
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedValuesByKey((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                          });
                        } else {
                          setSelectedValuesByKey((prev) => ({ ...prev, [key]: [] }));
                        }
                      }}
                    >
                      (All)
                    </Checkbox>
                    {isDateValues && dateTree ? (
                      <DateTree
                        tree={dateTree}
                        allValues={allValues}
                        selectedSet={selectedSet}
                        onSelectionChange={(nextSelected) => {
                          if (nextSelected.size === allValues.length) {
                            setSelectedValuesByKey((prev) => {
                              const updated = { ...prev };
                              delete updated[key];
                              return updated;
                            });
                          } else {
                            setSelectedValuesByKey((prev) => ({
                              ...prev,
                              [key]: Array.from(nextSelected),
                            }));
                          }
                        }}
                      />
                    ) : (
                      filteredValues.map((value) => {
                        const checked = selectedSet.has(value);
                        return (
                          <div key={value} style={{ paddingLeft: 4 }}>
                            <Checkbox
                              checked={checked}
                              onChange={(event) => {
                                const next = new Set(selectedSet);
                                if (event.target.checked) {
                                  next.add(value);
                                } else {
                                  next.delete(value);
                                }
                                if (next.size === allValues.length) {
                                  setSelectedValuesByKey((prev) => {
                                    const updated = { ...prev };
                                    delete updated[key];
                                    return updated;
                                  });
                                } else {
                                  setSelectedValuesByKey((prev) => ({
                                    ...prev,
                                    [key]: Array.from(next),
                                  }));
                                }
                              }}
                            >
                              {value}
                            </Checkbox>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: 'text',
              label: 'Text Filters',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {renderOperatorSelect(
                    wordOperators,
                    textFilter.operator,
                    (value) =>
                      setTextFiltersByKey((prev) => ({
                        ...prev,
                        [key]: { ...textFilter, operator: value as OperatorKey },
                      })),
                    operatorIcons as Record<string, React.ReactNode>
                  )}
                  <Input
                    size="small"
                    placeholder="Enter value"
                    value={textFilter.value}
                    onChange={(event) =>
                      setTextFiltersByKey((prev) => ({
                        ...prev,
                        [key]: { ...textFilter, value: event.target.value },
                      }))
                    }
                  />
                </div>
              ),
            },
            {
              key: 'numeric',
              label: 'Numeric Filters',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {renderOperatorSelect(
                    numericOperators,
                    numberFilter.operator,
                    (value) =>
                      setNumberFiltersByKey((prev) => ({
                        ...prev,
                        [key]: { ...numberFilter, operator: value as NumericOperatorKey },
                      })),
                    numericOperatorIcons as Record<string, React.ReactNode>
                  )}
                  <Input
                    size="small"
                    placeholder="Value"
                    value={numberFilter.value}
                    onChange={(event) =>
                      setNumberFiltersByKey((prev) => ({
                        ...prev,
                        [key]: { ...numberFilter, value: event.target.value },
                      }))
                    }
                  />
                  {numberFilter.operator === 'between' && (
                    <Input
                      size="small"
                      placeholder="And"
                      value={numberFilter.value2}
                      onChange={(event) =>
                        setNumberFiltersByKey((prev) => ({
                          ...prev,
                          [key]: { ...numberFilter, value2: event.target.value },
                        }))
                      }
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'date',
              label: 'Date Filters',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {renderOperatorSelect(
                    dateOperators,
                    dateFilter.operator,
                    (value) =>
                      setDateFiltersByKey((prev) => ({
                        ...prev,
                        [key]: { ...dateFilter, operator: value as DateOperatorKey },
                      })),
                    dateOperatorIcons as Record<string, React.ReactNode>
                  )}
                  <DatePicker
                    size="small"
                    style={{ width: '100%' }}
                    value={dateFilter.value ? dayjs(dateFilter.value) : null}
                    onChange={(_, dateString) =>
                      setDateFiltersByKey((prev) => ({
                        ...prev,
                        [key]: { ...dateFilter, value: String(dateString || '') },
                      }))
                    }
                  />
                  {dateFilter.operator === 'between' && (
                    <DatePicker
                      size="small"
                      style={{ width: '100%' }}
                      value={dateFilter.value2 ? dayjs(dateFilter.value2) : null}
                      onChange={(_, dateString) =>
                        setDateFiltersByKey((prev) => ({
                          ...prev,
                          [key]: { ...dateFilter, value2: String(dateString || '') },
                        }))
                      }
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <Button size="small" onClick={() => clearColumnFilters(key)}>
            Clear Filter
          </Button>
          <Button
            size="small"
            onClick={() => setFilterOpenByKey((prev) => ({ ...prev, [key]: false }))}
          >
            Close
          </Button>
        </div>
      </div>
    );

    return (
      <Dropdown
        trigger={['click']}
        open={!!filterOpenByKey[key]}
        onOpenChange={(open, info) => {
          if (info?.source !== 'trigger') {
            return;
          }
          setFilterOpenByKey((prev) => ({ ...prev, [key]: open }));
        }}
        dropdownRender={() => dropdownContent}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          tabIndex={0}
          role="button"
          aria-label="Filter"
        >
          {buildFilterIcon(isColumnFilterActive(key))}
        </span>
      </Dropdown>
    );
  };

  const columnsWithSearch: ColumnsType<T> = useMemo(
    () =>
      columns.map((col, index) => {
        const key = getColumnKey(col, index);
        const searchable = col.searchable !== false; // default true

        const existingTitle = col.title as React.ReactNode;

        return {
          ...col,
          width: col.width, // Explicitly preserve width
          onHeaderCell: col.onHeaderCell, // Explicitly preserve onHeaderCell for resizing
          title: splitHeader ? (
            <div style={{ ...headerLabelRowStyle, justifyContent: 'space-between' }}>
              <span style={headerLabelStyle}>{existingTitle}</span>
              {buildAdvancedFilterDropdown(col, index)}
            </div>
          ) : (
            <div style={headerContainerStyle}>
              <div style={{ ...headerLabelRowStyle, justifyContent: 'space-between' }}>
                <span style={headerLabelStyle}>{existingTitle}</span>
                {buildAdvancedFilterDropdown(col, index)}
              </div>
              {searchable ? buildFilterInput(col, index) : null}
            </div>
          ),
        };
      }),
    [
      columns,
      columnFilters,
      columnOperators,
      splitHeader,
      valueOptionsByKey,
      valuesSearchByKey,
      selectedValuesByKey,
      textFiltersByKey,
      numberFiltersByKey,
      dateFiltersByKey,
      filterOpenByKey,
      dateTreeOpenByKey,
    ]
  );

  const searchRowCells = useMemo(() => {
    if (!splitHeader) return undefined;
    return columns.map((col, index) => {
      const searchable = col.searchable !== false;
      if (!searchable) return null;
      return buildFilterInput(col, index);
    });
  }, [columns, columnFilters, columnOperators, splitHeader]);

  const matchesFilter = (
    cellValue: unknown,
    filterValue: string,
    operatorKey?: OperatorKey
  ) => {
    const rawCell = cellValue ?? '';
    if (
      operatorKey === 'eq' ||
      operatorKey === 'neq' ||
      operatorKey === 'lt' ||
      operatorKey === 'lte' ||
      operatorKey === 'gt' ||
      operatorKey === 'gte'
    ) {
      const left = Number.parseFloat(String(rawCell).replace(/,/g, ''));
      const right = Number.parseFloat(String(filterValue).replace(/,/g, ''));
      if (!Number.isFinite(left) || !Number.isFinite(right)) {
        return false;
      }
      switch (operatorKey) {
        case 'neq':
          return left !== right;
        case 'lt':
          return left < right;
        case 'lte':
          return left <= right;
        case 'gt':
          return left > right;
        case 'gte':
          return left >= right;
        case 'eq':
        default:
          return left === right;
      }
    }

    const left = String(rawCell).toLowerCase();
    const right = String(filterValue).toLowerCase();
    switch (operatorKey) {
      case 'not_like':
      case 'not_contains':
        return !left.includes(right);
      case 'begins':
        return left.startsWith(right);
      case 'ends':
        return left.endsWith(right);
      case 'like':
      case 'contains':
      default:
        return left.includes(right);
    }
  };

  const applyValuesFilter = (cellValue: unknown, selectedValues: string[] | undefined) => {
    if (selectedValues === undefined) return true;
    if (selectedValues.length === 0) return false;
    const label = normalizeValueLabel(cellValue);
    return selectedValues.includes(label);
  };

  const applyTextFilter = (cellValue: unknown, filter: { operator: OperatorKey; value: string } | undefined) => {
    if (!filter?.value) return true;
    return matchesFilter(cellValue, filter.value, filter.operator);
  };

  const applyNumericFilter = (
    cellValue: unknown,
    filter: { operator: NumericOperatorKey; value: string; value2: string } | undefined
  ) => {
    if (!filter) return true;
    const hasValue = filter.value.trim() !== '';
    const hasValue2 = filter.value2.trim() !== '';
    if (!hasValue && !hasValue2) return true;
    if (filter.operator === 'between' && (!hasValue || !hasValue2)) return true;

    const left = parseNumericValue(cellValue);
    const right = parseNumericValue(filter.value);
    const right2 = parseNumericValue(filter.value2);
    if (left === null || right === null) return false;

    switch (filter.operator) {
      case 'neq':
        return left !== right;
      case 'lt':
        return left < right;
      case 'lte':
        return left <= right;
      case 'gt':
        return left > right;
      case 'gte':
        return left >= right;
      case 'between':
        if (right2 === null) return false;
        return left >= Math.min(right, right2) && left <= Math.max(right, right2);
      case 'eq':
      default:
        return left === right;
    }
  };

  const applyDateFilter = (
    cellValue: unknown,
    filter: { operator: DateOperatorKey; value: string; value2: string } | undefined
  ) => {
    if (!filter) return true;
    const hasValue = filter.value.trim() !== '';
    const hasValue2 = filter.value2.trim() !== '';
    if (!hasValue && !hasValue2) return true;
    if (filter.operator === 'between' && (!hasValue || !hasValue2)) return true;

    const left = parseDateValue(cellValue);
    const right = parseDateValue(filter.value);
    const right2 = parseDateValue(filter.value2);
    if (!left || !right) return false;

    const leftTime = left.setHours(0, 0, 0, 0);
    const rightTime = right.setHours(0, 0, 0, 0);
    const right2Time = right2 ? right2.setHours(0, 0, 0, 0) : null;

    switch (filter.operator) {
      case 'before':
        return leftTime < rightTime;
      case 'after':
        return leftTime > rightTime;
      case 'between':
        if (right2Time == null) return false;
        return leftTime >= Math.min(rightTime, right2Time) && leftTime <= Math.max(rightTime, right2Time);
      case 'on':
      default:
        return leftTime === rightTime;
    }
  };

  const filteredData: T[] = useMemo(() => {
    const hasQuickFilters = Object.values(columnFilters).some((v) => v);
    const hasAdvancedFilters =
      Object.values(selectedValuesByKey).some((values) => values !== undefined) ||
      Object.values(textFiltersByKey).some((filter) => filter?.value) ||
      Object.values(numberFiltersByKey).some((filter) => filter?.value || filter?.value2) ||
      Object.values(dateFiltersByKey).some((filter) => filter?.value || filter?.value2);

    if (!hasQuickFilters && !hasAdvancedFilters) {
      return data;
    }

    return data.filter((record) =>
      searchColumns.every((col, index) => {
        const key = getColumnKey(col, index);
        const filterValue = columnFilters[key];
        const operatorKey = columnOperators[key] || getDefaultOperator();
        const cellValue = getColumnValue(record, col);

        if (filterValue && !matchesFilter(cellValue, filterValue, operatorKey)) {
          return false;
        }

        if (!applyValuesFilter(cellValue, selectedValuesByKey[key])) {
          return false;
        }

        if (!applyTextFilter(cellValue, textFiltersByKey[key])) {
          return false;
        }

        if (!applyNumericFilter(cellValue, numberFiltersByKey[key])) {
          return false;
        }

        if (!applyDateFilter(cellValue, dateFiltersByKey[key])) {
          return false;
        }

        return true;
      })
    );
  }, [
    data,
    searchColumns,
    columnFilters,
    columnOperators,
    selectedValuesByKey,
    textFiltersByKey,
    numberFiltersByKey,
    dateFiltersByKey,
  ]);

  return {
    columnsWithSearch,
    filteredData,
    searchRowCells,
  };
}
